import { MoreHorizontal, Search, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import type { RoutingConfig } from '../types';

interface RoutingTableProps {
	rows: RoutingConfig[];
	onView: (row: RoutingConfig) => void;
	onToggle: (id: number) => void;
	onEdit: (row: RoutingConfig) => void;
	activeCount: number;
	inactiveCount: number;
}

export function RoutingTable({
	rows: initialRows,
	onView,
	onToggle,
	onEdit,
	activeCount,
	inactiveCount,
}: RoutingTableProps) {
	const [search, setSearch] = useState('');
	const [showFilters, setShowFilters] = useState(false);
	const [filterSub, setFilterSub] = useState('');
	const [filterStatus, setFilterStatus] = useState('');

	const filtered = initialRows.filter((r) => {
		const matchSearch =
			search === '' ||
			r.name.toLowerCase().includes(search.toLowerCase()) ||
			r.type.toLowerCase().includes(search.toLowerCase());
		const matchSub = filterSub === '' || r.subdomain === filterSub;
		const matchStatus = filterStatus === '' || r.status === filterStatus;
		return matchSearch && matchSub && matchStatus;
	});

	const clearFilters = () => {
		setFilterSub('');
		setFilterStatus('');
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<div className="relative">
						<Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="Search by Name Type"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="h-9 w-56 pl-8 text-sm"
						/>
					</div>
					<span className="text-xs text-muted-foreground">
						{activeCount} active · {inactiveCount} inactive
					</span>
					<Button
						variant={showFilters ? 'secondary' : 'outline'}
						size="sm"
						className="gap-1.5"
						onClick={() => setShowFilters((f) => !f)}
					>
						<SlidersHorizontal className="h-3.5 w-3.5" />
						{showFilters ? 'Hide Filters' : 'Show Filters'}
					</Button>
				</div>
			</div>

			{showFilters && (
				<div className="flex items-end gap-3 rounded-lg border bg-muted/30 p-3">
					<div className="flex-1">
						<span className="mb-1 block text-xs text-muted-foreground">
							Subdomain
						</span>
						<Select value={filterSub} onValueChange={setFilterSub}>
							<SelectTrigger className="h-9">
								<SelectValue placeholder="Select Subdomain" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="Mail">Email</SelectItem>
								<SelectItem value="Push">Push</SelectItem>
								<SelectItem value="SMS">SMS</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="flex-1">
						<span className="mb-1 block text-xs text-muted-foreground">
							Status
						</span>
						<Select value={filterStatus} onValueChange={setFilterStatus}>
							<SelectTrigger className="h-9">
								<SelectValue placeholder="Select Status" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="Active">Active</SelectItem>
								<SelectItem value="Inactive">Inactive</SelectItem>
							</SelectContent>
						</Select>
					</div>
					{(filterSub || filterStatus) && (
						<Button
							variant="outline"
							size="sm"
							className="h-9"
							onClick={clearFilters}
						>
							Clear filters
						</Button>
					)}
				</div>
			)}

			<p className="text-xs font-medium text-muted-foreground">
				Active configurations
			</p>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="w-12">S.No</TableHead>
						<TableHead>Name</TableHead>
						<TableHead>Type</TableHead>
						<TableHead>Subdomain</TableHead>
						<TableHead>Description</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Created</TableHead>
						<TableHead className="w-10" />
					</TableRow>
				</TableHeader>
				<TableBody>
					{filtered.map((r, i) => (
						<TableRow
							key={r.id}
							className="cursor-pointer"
							onClick={() => onView(r)}
						>
							<TableCell className="text-muted-foreground">{i + 1}</TableCell>
							<TableCell className="font-medium">{r.name}</TableCell>
							<TableCell>
								<Badge variant="outline">{r.type}</Badge>
							</TableCell>
							<TableCell>{r.subdomain}</TableCell>
							<TableCell className="text-muted-foreground">{r.desc}</TableCell>
							<TableCell>
								<Badge
									variant={r.status === 'Active' ? 'default' : 'secondary'}
								>
									{r.status}
								</Badge>
							</TableCell>
							<TableCell className="text-muted-foreground">
								{r.created}
							</TableCell>
							<TableCell>
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											onClick={(e) => e.stopPropagation()}
										>
											<MoreHorizontal className="h-3.5 w-3.5" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem
											onClick={(e) => {
												e.stopPropagation();
												onView(r);
											}}
										>
											View
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={(e) => {
												e.stopPropagation();
												onEdit(r);
											}}
										>
											Edit
										</DropdownMenuItem>
										<DropdownMenuItem
											onClick={(e) => {
												e.stopPropagation();
												onToggle(r.id);
											}}
										>
											{r.status === 'Active' ? 'Deactivate' : 'Activate'}
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
